<template>
  <transition-group tag="ul" :class="containerKls" :name="nsList.b()">
    <li v-for="(file, index) in files" :key="file.uid || file.name" :class="[
      nsUpload.be('list', 'item'),
      nsUpload.is(file.status),
      { focusing },
    ]" :tabindex="disabled ? undefined : 0" :aria-disabled="disabled" role="button"
      @keydown.delete="!disabled && handleRemove(file)" @focus="focusing = true" @blur="focusing = false"
      @click="focusing = false">
      <slot :file="file" :index="index">
        <img v-if="
          listType === 'picture' ||
          (file.status !== 'uploading' && listType === 'picture-card')
        " :class="nsUpload.be('list', 'item-thumbnail')" :src="file.url" :crossorigin="crossorigin" alt="" />
        <div v-if="file.status === 'uploading' || listType !== 'picture-card'"
          :class="nsUpload.be('list', 'item-info')">
          <a :class="nsUpload.be('list', 'item-name')" @click.prevent="handlePreview(file)">
            <z-icon :class="nsIcon.m('document')">
              <Document />
            </z-icon>
            <span :class="nsUpload.be('list', 'item-file-name')" :title="file.name">
              {{ file.name }}
            </span>
          </a>
          <z-progress v-if="file.status === 'uploading'" :type="listType === 'picture-card' ? 'circle' : 'line'"
            :stroke-width="listType === 'picture-card' ? 6 : 2" :percentage="Number(file.percentage)"
            :style="listType === 'picture-card' ? '' : 'margin-top: 0.5rem'" />
        </div>

        <label :class="nsUpload.be('list', 'item-status-label')">
          <z-icon v-if="listType === 'text'" :class="[nsIcon.m('upload-success'), nsIcon.m('circle-check')]">
            <circle-check />
          </z-icon>
          <z-icon v-else-if="['picture-card', 'picture'].includes(listType)"
            :class="[nsIcon.m('upload-success'), nsIcon.m('check')]">
            <Check />
          </z-icon>
        </label>
        <z-icon v-if="!disabled" :class="nsIcon.m('close')" :aria-label="t('z.upload.delete')" role="button"
          tabindex="0" @click="handleRemove(file)" @keydown.enter.space.prevent="handleRemove(file)">
          <Close />
        </z-icon>
        <i v-if="!disabled" :class="nsIcon.m('close-tip')">{{
          t('z.upload.deleteTip')
        }}</i>
        <span v-if="listType === 'picture-card'" :class="nsUpload.be('list', 'item-actions')">
          <span :class="nsUpload.be('list', 'item-preview')" @click="handlePreview(file)">
            <z-icon :class="nsIcon.m('zoom-in')"><zoom-in /></z-icon>
          </span>
          <span v-if="!disabled" :class="nsUpload.be('list', 'item-delete')" @click="handleRemove(file)">
            <z-icon :class="nsIcon.m('delete')">
              <Delete />
            </z-icon>
          </span>
        </span>
      </slot>
    </li>
    <slot name="append" />
  </transition-group>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { ZIcon } from '@zoryn/components/icon'
import {
  Check,
  CircleCheck,
  Close,
  Delete,
  Document,
  ZoomIn,
} from '@zoryn/icons'
import { useLocale, useNamespace } from '@zoryn/hooks'
import ZProgress from '@zoryn/components/progress'
import { useFormDisabled } from '@zoryn/components/form'
import { uploadListEmits } from './upload-list'
import { NOOP, mutable } from '@zoryn/utils'

import type { UploadListProps } from './upload-list'
import type { UploadFile } from './upload'

defineOptions({
  name: 'ZUploadList',
})

const props = withDefaults(defineProps<UploadListProps>(), {
  files: () => mutable([]),
  disabled: undefined,
  handlePreview: NOOP,
  listType: 'text',
})
const emit = defineEmits(uploadListEmits)

const { t } = useLocale()
const nsUpload = useNamespace('upload')
const nsIcon = useNamespace('icon')
const nsList = useNamespace('list')
const disabled = useFormDisabled()

const focusing = ref(false)

const containerKls = computed(() => [
  nsUpload.b('list'),
  nsUpload.bm('list', props.listType),
  nsUpload.is('disabled', disabled.value),
])

const handleRemove = (file: UploadFile) => {
  emit('remove', file)
}
</script>
